import { Alert, Button } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Fmt } from "../../../../../formatters";
import { usePrimaryOrganization } from "../../../../../hooks";
import { useClosedMessageTemplate } from "../../../../../hooks/useTemplates";
import { Callout, CalloutVariant } from "../../../../Callout";
import { AttachmentList } from "../../../../organisms/AttachmentList";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { useDataRequestAttachments } from "../../../../../hooks/useDataRequestAttachments";
import { createDataRequestZip, getZipFileCount } from "../../../../../util/accessRequestZipUtils";
import { usePrivacyCenter } from "../../../../../hooks/usePrivacyCenter";
import { AlertCard } from "@truevault/aquilo";
import { WorkflowType } from "../../../../../../common/service/server/dto/RegulationRequestTypeDto";

export const SendData = () => {
  const [org] = usePrimaryOrganization();
  const {
    request,
    attachment,
    setStepCompleted,
    generatedZip,
    setGeneratedZip,
    personalIdentifiers,
    setPersonalIdentifiers,
  } = useDataRequestProcessingContext();

  const [template] = useClosedMessageTemplate(org.id, request.id);
  const fullName = Fmt.DataSubject.fmtFullName(request);
  const [privacyCenter] = usePrivacyCenter(org.id);
  const { attachments, isLoading: isLoadingAttachments } = useDataRequestAttachments(
    org.id,
    request.id,
  );
  const [isGeneratingZip, setIsGeneratingZip] = useState(false);

  // Check if any vendor has data (not NO_DATA, AUTOMATION_NO_DATA, or DATA_IN_FORM)
  const hasDataFromAnyVendor = useMemo(
    () =>
      request.vendorOutcomes?.some(
        (vo) =>
          vo.outcome !== "NO_DATA" &&
          vo.outcome !== "AUTOMATION_NO_DATA" &&
          vo.outcome !== "DATA_IN_FORM",
      ) ?? true,
    [request.vendorOutcomes],
  );

  const uploadEl = useRef<HTMLInputElement>(null);
  const removeFile = (index: number) => {
    attachment?.removeFile(index);
  };

  const fileUploadNeeded = useMemo(() => {
    // If we have a generated zip, no need to show file upload
    if (generatedZip) {
      return false;
    }

    return (
      hasDataFromAnyVendor &&
      request.consumerGroupResult !== "NOT_FOUND" &&
      request.consumerGroupResult !== "PROCESSED_AS_SERVICE_PROVIDER"
    );
  }, [hasDataFromAnyVendor, request.consumerGroupResult, generatedZip]);

  const names = useMemo(() => attachment?.files?.map((f) => f.name) ?? [], [attachment]);

  // Check if this is an access request type that needs a zip file
  const isAccessRequest = useMemo(() => {
    const workflowType = request.dataRequestType?.workflowType;
    return (
      workflowType === WorkflowType.KNOW ||
      workflowType === WorkflowType.PORTABILITY ||
      workflowType === WorkflowType.PROFILING_INFORMATION
    );
  }, [request.dataRequestType?.workflowType]);

  // Regenerate zip file if it doesn't exist but we have the data
  useEffect(() => {
    const generateZipIfNeeded = async () => {
      // Wait for attachments to finish loading
      if (isLoadingAttachments) {
        return;
      }

      // Don't generate if we already have one or are currently generating
      if (generatedZip || isGeneratingZip) {
        return;
      }

      // Don't generate if request was already contacted
      if (request.requestorContacted) {
        return;
      }

      // For access requests, we should always generate a zip if:
      // 1. Consumer was found (not NOT_FOUND or PROCESSED_AS_SERVICE_PROVIDER)
      // 2. At least one vendor has data OR we have uploaded files
      const consumerFound =
        request.consumerGroupResult !== "NOT_FOUND" &&
        request.consumerGroupResult !== "PROCESSED_AS_SERVICE_PROVIDER";

      if (!isAccessRequest) {
        return;
      }

      if (!consumerFound) {
        return;
      }

      // Check if we have any uploaded data files or if vendors have data
      const uploadedFiles = attachments.filter((att) => att.status === "UPLOADED");
      const shouldGenerateZip = hasDataFromAnyVendor || uploadedFiles.length > 0;

      if (!shouldGenerateZip) {
        return;
      }

      setIsGeneratingZip(true);

      try {
        // Build custom fields array from request data (excluding address and phone)
        const customFields: Array<{ label: string; value: string }> = [];
        const excludedKeys = new Set([
          "address1",
          "address2",
          "city",
          "state",
          "zipCode",
          "country",
          "phoneNumber",
        ]);

        // Check for legacy organizationCustomInput field (same logic as PersonalIdentifiersForm)
        if (request.organizationCustomInput && org.customFieldEnabled && org.customFieldLabel) {
          customFields.push({
            label: org.customFieldLabel,
            value: request.organizationCustomInput,
          });
        }

        // Add custom fields from subjectCustomFieldsFmt
        if (request.subjectCustomFieldsFmt) {
          Object.entries(request.subjectCustomFieldsFmt).forEach(([label, value]) => {
            const keyToCheck = label
              .toLowerCase()
              .replace(/\s+/g, "")
              .replace(/[()]/g, "")
              .replace(/line1/g, "1")
              .replace(/line2/g, "2");

            const isExcluded =
              excludedKeys.has(keyToCheck) ||
              label.toLowerCase().includes("address") ||
              label.toLowerCase().includes("city") ||
              label.toLowerCase().includes("state") ||
              label.toLowerCase().includes("zip") ||
              label.toLowerCase().includes("country") ||
              label.toLowerCase().includes("phone");

            if (!isExcluded && value) {
              customFields.push({ label, value: String(value) });
            }
          });
        }

        // Merge personal identifiers from React state with request data from backend
        // This ensures we use the most up-to-date data from the backend for fields that were persisted

        // Extract phone number from all possible sources (same logic as PersonalIdentifiersForm)
        let extractedPhone = request.subjectFreePhone || "";
        if (!extractedPhone) {
          extractedPhone = request.subjectPhoneNumber || "";
          if (!extractedPhone && request.subjectCustomFields?.phoneNumber) {
            extractedPhone = request.subjectCustomFields.phoneNumber;
          }
          if (!extractedPhone && request.subjectCustomFieldsFmt) {
            const phoneField = Object.entries(request.subjectCustomFieldsFmt).find(([label]) =>
              label.toLowerCase().includes("phone"),
            );
            if (phoneField) {
              extractedPhone = phoneField[1] as string;
            }
          }
        }

        // Extract address from all possible sources (same logic as PersonalIdentifiersForm)
        let extractedAddress = request.subjectFreeTextAddress || "";
        if (!extractedAddress) {
          extractedAddress = request.subjectMailingAddress || "";
          if (!extractedAddress && request.subjectCustomFields) {
            const addressParts = [
              request.subjectCustomFields["address1"],
              request.subjectCustomFields["address2"],
              request.subjectCustomFields["city"],
              request.subjectCustomFields["state"],
              request.subjectCustomFields["zipCode"],
              request.subjectCustomFields["country"],
            ].filter(Boolean);
            if (addressParts.length > 0) {
              extractedAddress = addressParts.join(", ");
            }
          }
        }

        const fallbackData = {
          firstName: request.subjectFirstName || "",
          lastName: request.subjectLastName || "",
          email: request.subjectEmailAddress || "",
          phone: extractedPhone,
          subjectFreePhone: extractedPhone,
          postalAddress: extractedAddress,
          subjectFreeTextAddress: extractedAddress,
          onlineIdentifiers: request.subjectOnlineIdentifiers || "",
          customFields: customFields,
        };

        // Merge with personalIdentifiers from React state, preferring backend data for persisted fields
        // Note: firstName, lastName, and email come from request, but fall back to personalIdentifiers state if request fields are empty
        const formData = {
          firstName: personalIdentifiers?.firstName || fallbackData.firstName,
          lastName: personalIdentifiers?.lastName || fallbackData.lastName,
          email: request.subjectEmailAddress || "",
          // Prefer React state if available, otherwise use saved backend value
          phone: personalIdentifiers?.phone || fallbackData.phone,
          subjectFreePhone:
            personalIdentifiers?.phone || fallbackData.subjectFreePhone || fallbackData.phone,
          postalAddress: personalIdentifiers?.postalAddress || fallbackData.postalAddress,
          subjectFreeTextAddress:
            personalIdentifiers?.postalAddress ||
            fallbackData.subjectFreeTextAddress ||
            fallbackData.postalAddress,
          onlineIdentifiers:
            personalIdentifiers?.onlineIdentifiers || fallbackData.onlineIdentifiers,
          customFields: personalIdentifiers?.customFields || fallbackData.customFields,
        };

        setPersonalIdentifiers(formData);

        const requestorName =
          `${request.subjectFirstName || ""}${request.subjectLastName || ""}`.trim() || "requestor";
        const zipFile = await createDataRequestZip(requestorName, formData, attachments);
        const fileCount = getZipFileCount(attachments);

        setGeneratedZip({ file: zipFile, fileCount });

        // Clear any existing files and only keep the zip file
        // This prevents duplicate attachments (files are already in the zip)
        const currentFileCount = attachment?.files?.length || 0;
        for (let i = currentFileCount - 1; i >= 0; i--) {
          attachment?.removeFile(i);
        }
        attachment?.addFiles([zipFile]);
      } catch (error) {
        console.error("[SendData] Error generating zip file:", error);
      } finally {
        setIsGeneratingZip(false);
      }
    };

    generateZipIfNeeded();
  }, [
    isLoadingAttachments,
    generatedZip,
    isGeneratingZip,
    attachments,
    request,
    privacyCenter,
    personalIdentifiers,
    setGeneratedZip,
    setPersonalIdentifiers,
    attachment,
    isAccessRequest,
    hasDataFromAnyVendor,
  ]);

  useEffect(() => {
    // If we have a generated zip, we can always proceed
    if (generatedZip) {
      setStepCompleted(true);
      return;
    }

    const canProceed = !fileUploadNeeded || names.length > 0;
    setStepCompleted(canProceed);
  }, [fileUploadNeeded, names.length, setStepCompleted, generatedZip]);

  return (
    <>
      {request.requestorContacted && (
        <div className="w-640">
          <Callout variant={CalloutVariant.Green} className="mb-std">
            Done! Message successfully sent to {fullName}. You can now close this request.
          </Callout>
        </div>
      )}

      {!request.requestorContacted && (
        <>
          {/* Display loading state while generating zip */}
          {isGeneratingZip && (
            <div className="mb-lg tw-boundary">
              <AlertCard variant="info">
                <div className="text-t3">Preparing zip file...</div>
              </AlertCard>
            </div>
          )}

          {/* Display zip file info if it exists */}
          {generatedZip && !isGeneratingZip && (
            <div className="mb-lg tw-boundary">
              <AlertCard variant="info">
                <div className="text-t3">
                  <strong>{generatedZip.file.name}</strong> containing{" "}
                  <strong>
                    {generatedZip.fileCount} file{generatedZip.fileCount !== 1 ? "s" : ""}
                  </strong>{" "}
                  is attached.
                </div>
              </AlertCard>
            </div>
          )}

          {fileUploadNeeded && (
            <div className="mb-lg">
              {attachment.fileError && (
                <div className="mb-sm">
                  <Alert severity="error">{attachment.fileError}</Alert>
                </div>
              )}

              <AttachmentList className="mb-md" onRemove={removeFile} attachmentNames={names} />

              <label htmlFor="note-upload" className="mr-md">
                <input
                  style={{ display: "none" }}
                  ref={uploadEl}
                  id="note-upload"
                  type="file"
                  multiple
                  onChange={(e) => {
                    if (e.target.files) {
                      attachment?.addFiles(Array.from(e.target.files));
                    }
                  }}
                />
                <Button
                  variant="contained"
                  className="btn-md"
                  onClick={() => uploadEl.current?.click()}
                >
                  Upload
                </Button>
              </label>
            </div>
          )}

          <Callout variant={CalloutVariant.Gray} className="mb-md">
            <div className="text-t3">
              <p>
                <strong>To: {request.subjectEmailAddress}</strong>
              </p>
              <p>
                <strong>Subject: A message about your request {request.id}</strong>
              </p>
              <div dangerouslySetInnerHTML={{ __html: template }} />
            </div>
          </Callout>
        </>
      )}
    </>
  );
};
